/* ============================================================================
 * launches-feed.js  -  window.IBLaunchFeed
 *
 * HC - Launches page: feed interpretation, validation and escaping - 2026-09-22
 *
 * Pure ES5 IIFE. No DOM, no gating, no network, and no dependency on any
 * promotions file. It reads the ib-launches-feed/v1 asset
 * (assets/launches-feed.json) and gives assets/ib-launches.js:
 *
 *   validateFeed(json)  -> { ok, error, launches, rejected: [{ index, id, reason }] }
 *   escapeHtml(s)       -> & < > " ' as entities (the card path's only escaper)
 *   imageUrl(url, w)    -> Shopify CDN width parameter appended
 *   imageSrcset(url, ws)-> "<url>&width=360 360w, ..."
 *   blocksToHtml(blocks)-> escaped <p>, <ul>/<ol>, <li> and <br> only
 *   destinationHref(l)  -> the validated destination.url
 *
 * The entry rules mirror the contract's reference_feed_validator.py exactly
 * (docs/plans/2026-09-22-launches-pipeline-contract.md section 6.3) on every
 * published case. On degenerate inputs it is deliberately never more lenient:
 * `$` never matches before a trailing newline, digits are ASCII only,
 * schemaVersion must be a string, and "blank" uses both languages' whitespace
 * sets (see BLANK_RE). The
 * `reason` of a rejection is the name of the first failing rule, or
 * `duplicate_id`. There is no date filtering of any kind (plan R10), and the
 * feed order is preserved.
 *
 * Revision d (2026-10-09): optional `tagline` (null or non-blank text, checked
 * right after `title`) and `banner` (null or an {url, alt} image that passes the
 * images url rule, checked right after `description`). A missing key means
 * null, so a schemaVersion 1.0.x feed still validates. The banner is never part
 * of `images`.
 *
 * Revision e (2026-10-13): optional `colours` (null or an object whose
 * background, text and accent are each a lowercase "#rrggbb" string, matched
 * as the whole string, with a WCAG 2.x text-on-background contrast ratio of at
 * least 4.5: relative luminance with the sRGB threshold 0.03928, compared
 * unrounded, exactly as the parser computes it). Rule `colours`, checked right
 * after `banner` and before `images`. Other keys on the object are ignored, as
 * on banner and image objects. A missing key means null, so a schemaVersion
 * 1.1.x feed still validates.
 *
 * Revision f (2026-10-14): optional `portraitBanner`, the 2:3 banner (null or
 * an {url, alt} image that passes the images url rule, exactly like `banner`).
 * Rule `portraitBanner`, checked right after `banner` and before `colours`.
 * Other keys on the object are ignored, as on banner. A missing key means
 * null, so a schemaVersion 1.2.x feed still validates. It is never part of
 * `images` either.
 * ==========================================================================*/

window.IBLaunchFeed = (function () {
  'use strict';

  var SCHEMA = 'ib-launches-feed/v1';
  var REGION_KEYS = ['prairie', 'bc', 'ontario', 'new-brunswick', 'atlantic', 'east', 'quebec'];
  var IMAGE_PREFIX = 'https://cdn.shopify.com/s/files/1/0442/3516/1766/';
  var STOREFRONT = 'https://internationalbeauty.ca/';
  var KINDS = ['brand', 'product', 'collection'];
  var DESTINATION_KINDS = ['product', 'collection'];

  var ID_RE = /^[0-9a-z]{6,12}$/;
  var HANDLE_RE = /^[a-z0-9][a-z0-9_-]*$/;
  var DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
  // Whitespace is the UNION of JavaScript's \s (which adds U+FEFF) and Python's
  // str.isspace() / re \s (which add U+001C-U+001F and U+0085), so this
  // validator is never more lenient than reference_feed_validator.py on blank
  // text or on whitespace inside image URLs.
  var BLANK_RE = /^[\s\u001c-\u001f\u0085]*$/;
  var URL_BAD_CHARS_RE = /[\s\u001c-\u001f\u0085"'<>\\]/;
  // Revision e. No m flag, so `$` is the end of the string (Python's
  // fullmatch): "#f7cdd4\n" never passes.
  var HEX_COLOUR_RE = /^#[0-9a-f]{6}$/;
  var COLOUR_KEYS = ['background', 'text', 'accent'];
  var MIN_CONTRAST = 4.5;

  var hasOwn = Object.prototype.hasOwnProperty;

  /* ---------------------------------------------------------------------
   * Small type helpers (own properties only, so a key such as
   * "constructor" can never be read off Object.prototype).
   * ------------------------------------------------------------------- */
  function isArray(v) {
    return Object.prototype.toString.call(v) === '[object Array]';
  }
  function isObject(v) {
    return v !== null && typeof v === 'object' && !isArray(v);
  }
  function get(obj, key) {
    return (isObject(obj) && hasOwn.call(obj, key)) ? obj[key] : undefined;
  }
  function isNil(v) {
    return v === null || v === undefined;
  }
  function isString(v) {
    return typeof v === 'string';
  }
  function isBoolean(v) {
    return typeof v === 'boolean';
  }
  function nonBlank(v) {
    return isString(v) && !BLANK_RE.test(v);
  }
  function inList(list, v) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] === v) return true;
    }
    return false;
  }
  function every(list, fn) {
    for (var i = 0; i < list.length; i++) {
      if (!fn(list[i])) return false;
    }
    return true;
  }

  /* ---------------------------------------------------------------------
   * Entry rules (contract 6.3 / reference_feed_validator.py)
   * ------------------------------------------------------------------- */
  function blockOk(b) {
    if (!isObject(b)) return false;
    var type = get(b, 'type');
    if (type === 'paragraph') return nonBlank(get(b, 'text'));
    if (type === 'list') {
      var items = get(b, 'items');
      return isBoolean(get(b, 'ordered')) && isArray(items) && items.length > 0 && every(items, nonBlank);
    }
    return false;
  }

  function blocksOk(v) {
    return isArray(v) && v.length > 0 && every(v, blockOk);
  }

  function imageOk(img) {
    if (!isObject(img)) return false;
    var url = get(img, 'url');
    return isString(url) &&
      url.indexOf(IMAGE_PREFIX) === 0 &&
      !URL_BAD_CHARS_RE.test(url) &&
      isString(get(img, 'alt'));
  }

  function hexColourOk(v) {
    return isString(v) && HEX_COLOUR_RE.test(v);
  }

  // WCAG 2.x relative luminance of a "#rrggbb" string (sRGB threshold
  // 0.03928), in the reference's order of operations.
  function relativeLuminance(hex6) {
    var out = [];
    for (var k = 1; k <= 5; k += 2) {
      var c = parseInt(hex6.slice(k, k + 2), 16) / 255;
      out.push(c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    }
    return 0.2126 * out[0] + 0.7152 * out[1] + 0.0722 * out[2];
  }

  // WCAG 2.x contrast ratio of two "#rrggbb" strings, unrounded.
  function contrastRatio(a, b) {
    var la = relativeLuminance(a);
    var lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  function coloursOk(c) {
    if (isNil(c)) return true;
    if (!isObject(c)) return false;
    for (var i = 0; i < COLOUR_KEYS.length; i++) {
      if (!hexColourOk(get(c, COLOUR_KEYS[i]))) return false;
    }
    return contrastRatio(get(c, 'text'), get(c, 'background')) >= MIN_CONTRAST;
  }

  function regionOk(r) {
    return isString(r) && inList(REGION_KEYS, r);
  }

  // Name of the first failing rule, or null when the entry is valid.
  function entryProblem(e) {
    if (!isObject(e)) return 'entry';
    var id = get(e, 'id');
    if (!(isString(id) && ID_RE.test(id))) return 'id';
    if (!inList(KINDS, get(e, 'kind'))) return 'kind';
    if (!nonBlank(get(e, 'title'))) return 'title';
    var tagline = get(e, 'tagline');
    if (!(isNil(tagline) || nonBlank(tagline))) return 'tagline';
    var brand = get(e, 'brand');
    if (!(isNil(brand) || nonBlank(brand))) return 'brand';
    if (!blocksOk(get(e, 'summary'))) return 'summary';
    var highlights = get(e, 'highlights');
    if (!(isArray(highlights) && every(highlights, nonBlank))) return 'highlights';
    var description = get(e, 'description');
    if (!(isNil(description) || blocksOk(description))) return 'description';
    var banner = get(e, 'banner');
    if (!(isNil(banner) || imageOk(banner))) return 'banner';
    var portraitBanner = get(e, 'portraitBanner');
    if (!(isNil(portraitBanner) || imageOk(portraitBanner))) return 'portraitBanner';
    if (!coloursOk(get(e, 'colours'))) return 'colours';
    var images = get(e, 'images');
    if (!(isArray(images) && images.length > 0 && every(images, imageOk))) return 'images';
    var dest = get(e, 'destination');
    if (!isObject(dest) || !inList(DESTINATION_KINDS, get(dest, 'kind'))) return 'destination';
    var handle = get(dest, 'handle');
    if (!(isString(handle) && HANDLE_RE.test(handle))) return 'destination';
    if (get(dest, 'url') !== STOREFRONT + get(dest, 'kind') + 's/' + handle) return 'destination';
    var launchDate = get(e, 'launchDate');
    if (!(isNil(launchDate) || (isString(launchDate) && DATE_RE.test(launchDate)))) return 'launchDate';
    var regions = get(e, 'restrictedRegions');
    if (!(isArray(regions) && every(regions, regionOk))) return 'restrictedRegions';
    if (!isBoolean(get(e, 'matrixOverridable'))) return 'matrixOverridable';
    var trace = get(e, 'trace');
    if (!(isObject(trace) && get(trace, 'taskId') === id)) return 'trace';
    return null;
  }

  function envelopeOk(json) {
    if (!isObject(json)) return false;
    if (get(json, 'schema') !== SCHEMA) return false;
    // A string only: String(['1.0']) would otherwise coerce an array to '1.0'.
    var version = get(json, 'schemaVersion');
    if (!isString(version) || version.indexOf('1.') !== 0) return false;
    return isArray(get(json, 'launches'));
  }

  function validateFeed(json) {
    if (!envelopeOk(json)) {
      return { ok: false, error: 'invalid_envelope', launches: [], rejected: [] };
    }
    var list = json.launches;
    var seen = Object.create(null);
    var launches = [];
    var rejected = [];
    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      var reason = entryProblem(entry);
      if (!reason && seen[entry.id]) reason = 'duplicate_id';
      if (reason) {
        rejected.push({
          index: i,
          id: (isObject(entry) && isString(get(entry, 'id'))) ? entry.id : null,
          reason: reason
        });
        continue;
      }
      seen[entry.id] = true;
      launches.push(entry);
    }
    return { ok: true, error: null, launches: launches, rejected: rejected };
  }

  /* ---------------------------------------------------------------------
   * Rendering helpers
   * ------------------------------------------------------------------- */
  function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function imageUrl(url, width) {
    var s = String(url);
    return s + (s.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function imageSrcset(url, widths) {
    var parts = [];
    for (var i = 0; i < widths.length; i++) {
      parts.push(imageUrl(url, widths[i]) + ' ' + widths[i] + 'w');
    }
    return parts.join(', ');
  }

  // Plain text -> escaped HTML; a single "\n" (an authored <br>) -> <br>.
  function textToHtml(text) {
    return escapeHtml(text).split('\n').join('<br>');
  }

  function blocksToHtml(blocks) {
    if (!isArray(blocks)) return '';
    var out = '';
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (!isObject(b)) continue;
      if (b.type === 'paragraph') {
        out += '<p>' + textToHtml(b.text) + '</p>';
      } else if (b.type === 'list' && isArray(b.items)) {
        var tag = b.ordered === true ? 'ol' : 'ul';
        var items = '';
        for (var j = 0; j < b.items.length; j++) {
          items += '<li>' + textToHtml(b.items[j]) + '</li>';
        }
        out += '<' + tag + '>' + items + '</' + tag + '>';
      }
    }
    return out;
  }

  function destinationHref(launch) {
    return launch.destination.url;
  }

  return {
    SCHEMA: SCHEMA,
    REGION_KEYS: REGION_KEYS.slice(),
    IMAGE_PREFIX: IMAGE_PREFIX,
    validateFeed: validateFeed,
    entryProblem: entryProblem,
    escapeHtml: escapeHtml,
    imageUrl: imageUrl,
    imageSrcset: imageSrcset,
    blocksToHtml: blocksToHtml,
    destinationHref: destinationHref
  };
})();
